import React, { useState, useEffect, useCallback } from 'react';
import styles from './MobileAppBanner.module.scss';

type MobileDeviceType = 'ios' | 'android' | 'unknown';

type MobileAppBannerProps = {
  /**
   * The deep link URL to open the app directly to a specific screen or resource.
   * This should be a universal/app link that works for both iOS and Android.
   * Used as the href for the "Open" button in the banner.
   * Example: "easyweddingsau://"
   */
  deepLinkUrl?: string;
  /** The number of days to remember when user dismisses the banner */
  cookieExpiryDays?: number;
};

/**
 * A mobile app banner component that appears on page load for mobile devices.
 * Detects the user's mobile OS and shows appropriate app store links and browser icons.
 * Uses cookies to remember user preferences and only shows on mobile devices.
 * 
 * @param deepLinkUrl - The deep link URL to open the app directly to a specific screen or resource (used for both iOS and Android)
 * @param cookieExpiryDays - How many days to remember user's dismissal preference
 * @returns A mobile app promotion banner with device-specific content
 */
const MobileAppBanner: React.FC<MobileAppBannerProps> = ({
  deepLinkUrl,
  cookieExpiryDays = 2
}) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [deviceType, setDeviceType] = useState<MobileDeviceType>('unknown');
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  /**
   * Detects the mobile operating system from the user agent string.
   * Used to determine which app store link and browser icon to display.
   * 
   * @returns The detected mobile OS type or 'unknown' for non-mobile devices
   */
  const getMobileOperatingSystem = useCallback((): MobileDeviceType => {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

    if (/android/i.test(userAgent)) {
      return 'android';
    }

    if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
      return 'ios';
    }

    return 'unknown';
  }, []);

  /**
   * Gets a cookie value by name from the document cookies.
   * 
   * @param name - The name of the cookie to retrieve
   * @returns The cookie value or undefined if not found
   */
  const getCookie = useCallback((name: string): string | undefined => {
    const value = "; " + document.cookie;
    const parts = value.split("; " + name + "=");
    if (parts.length === 2) {
      return parts.pop()?.split(";").shift();
    }
    return undefined;
  }, []);

  /**
   * Sets a cookie with the specified name, value, and expiry days.
   * 
   * @param name - The name of the cookie to set
   * @param value - The value to store in the cookie
   * @param days - Number of days until the cookie expires
   */
  const setCookie = useCallback((name: string, value: string | boolean, days: number): void => {
    const date = new Date();
    date.setTime(date.getTime() + 24 * 60 * 60 * 1000 * days);
    document.cookie = `${name}=${value};path=/;expires=${date.toUTCString()}`;
  }, []);

  /**
   * Converts a string cookie value to a boolean.
   * 
   * @param value - The string value from the cookie
   * @returns True if the string equals 'true', false otherwise
   */
  const cookieAsBoolean = useCallback((value: string): boolean => {
    return value === 'true';
  }, []);

  /**
   * Checks if the banner has been hidden by the user via cookie preference.
   * Also initializes the cookie if it doesn't exist.
   * 
   * @param shouldHide - If true, sets the cookie to hide the banner
   * @returns True if the banner should be hidden, false otherwise
   */
  const isHiddenByCookie = useCallback((shouldHide = false): boolean => {
    let value = getCookie('mobile-app-banner-hidden');

    if (value === undefined) {
      setCookie('mobile-app-banner-hidden', false, cookieExpiryDays);
    }

    if (shouldHide) {
      setCookie('mobile-app-banner-hidden', true, cookieExpiryDays);
    }

    value = getCookie('mobile-app-banner-hidden');
    return value ? cookieAsBoolean(value) : false;
  }, [getCookie, setCookie, cookieExpiryDays, cookieAsBoolean]);

  /**
   * Checks if the banner should be shown on load.
   * Shows the banner immediately if not hidden by cookie preference.
   */
  const shouldShowBanner = useCallback((): boolean => {
    return !isHiddenByCookie();
  }, [isHiddenByCookie]);

  /**
   * Handles hiding the banner when user clicks dismiss.
   * Updates the cookie preference and hides the component.
   */
  const handleHideBanner = useCallback(() => {
    setIsVisible(false);
    isHiddenByCookie(true);
    setIsLoaded(false);
  }, [isHiddenByCookie]);

  /**
   * Gets the appropriate browser icon URL based on the detected device type.
   * 
   * @returns The browser icon URL for the detected mobile platform
   */
  const getBrowserIconUrl = useCallback((): string => {
    const iconPath = deviceType === 'ios' 
      ? 'https://v6-cdn.easyweddings.com/images/browsers/safari.svg'
      : 'https://v6-cdn.easyweddings.com/images/browsers/chrome.svg';
    
    return iconPath;
  }, [deviceType]);

  // Initialize device detection and show banner on load
  useEffect(() => {
    const detectedDevice = getMobileOperatingSystem();
    setDeviceType(detectedDevice);

    // Show banner immediately for mobile devices if not hidden by cookie
    if (detectedDevice !== 'unknown' && !isLoaded && shouldShowBanner()) {
      setIsVisible(true);
      setIsLoaded(true);
    }
  }, [getMobileOperatingSystem, isLoaded, shouldShowBanner]);

  // Don't render anything for non-mobile devices or if hidden by cookie
  if (deviceType === 'unknown' || !isVisible || !deepLinkUrl) {
    return null;
  }

  return (
    <div id="mobile-app-banner" className={styles.container}>
      <div className={styles.drawer} id="mab-drawer">
        <span className={styles.drawerTitle}>See Easy Weddings in...</span>
        <hr />
        
        <div className={`${styles.row} ${styles.alignItemsCenter}`}>
          <div className={styles.col3}>
            <div className={styles.drawerIcon}>
              <img 
                src="https://v6-cdn.easyweddings.com/images/App_Icon_Native_Drawer_Mobile_150x150.svg" 
                alt="Download the Easy Weddings App" 
                width="50" 
                height="50" 
              />
            </div>
          </div>
          <div className={styles.col4}>
            <span className={styles.drawerLabel}>EW App</span>
          </div>
          <div className={`${styles.col5} ${styles.appBannerButton}`}>
            <button
              id="mab-app-button"
              className={`${styles.btn} ${styles.btnPrimary} ${styles.w100}`}
              type="button"
              onClick={() => {
                if (!deepLinkUrl) return;
                const now = Date.now();
                window.location.href = deepLinkUrl;
                setTimeout(() => {
                  // If the app did not open, redirect to store
                  if (Date.now() - now < 2000) {
                    window.location.href =
                      deviceType === 'ios'
                        ? 'https://itunes.apple.com/au/app/easy-weddings-directory/id433611621'
                        : 'https://play.google.com/store/apps/details?id=au.com.easyweddings.directory';
                  }
                }, 1500);
              }}
            >
              Open
            </button>
          </div>
        </div>
        
        <div className={styles.mabVspacer}></div>
        
        <div className={`${styles.row} ${styles.alignItemsCenter}`}>
          <div className={styles.col3}>
            <div className={styles.drawerIcon}>
              <img 
                id="mab-browser-icon"
                src={getBrowserIconUrl()} 
                alt="Continue browsing Easy Weddings" 
                width="50" 
                height="50" 
              />
            </div>
          </div>
          <div className={styles.col4}>
            <span className={styles.drawerLabel}>Browser</span>
          </div>
          <div className={`${styles.col5} ${styles.appBannerButton}`}>
            <button 
              className={`${styles.btn} ${styles.btnOutlineSecondary} ${styles.w100}`}
              onClick={handleHideBanner}
              type="button"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileAppBanner;